import {AppUrls} from 'shared/config/model/urls';

import React, {ReactNode, useState} from 'react';
import {useSelector} from 'react-redux';
import {Navigate} from 'react-router';
import {Link} from 'react-router-dom';
import cn from 'classnames';
import {t} from '@lingui/core/macro';

import ChangeEmailModal from 'features/profile/ui/Profile/ChangeEmailModal';
import Button from 'shared/ui/Button/Button/Button';
import SummaryHeader from 'shared/ui/SummaryHeader/SummaryHeader';
import Email from 'images/emails.png';
import {
    useEmailConfirmation,
    useSubscribeEmail,
    userSelector,
    EmailErrorType,
    getEmailScreenPriority,
} from 'entities/user';

import AskEmail from '../AskEmail/AskEmail';
import HeaderEmail from '../HeaderEmail/HeaderEmail';
import AnswerEmail from '../AnswerEmail/AnswerEmail';

import {isTranslate} from '../../../../../shared/lib/lang';

import styles from './EmailProblem.module.scss';

type EmailErrorData = {
    gradient?: string;
    textHeader: ReactNode;
    answer: ReactNode;
    buttonText?: string;
    onClick?: () => void;
};

const EmailProblem: React.FC = () => {
    const user = useSelector(userSelector);
    const [emailModalOpen, setEmailModalOpen] = useState(false);

    const error = user?.last_email_error as EmailErrorType | undefined;
    const emailConfirmed = user?.email_confirmed;

    const confirmEmail = useEmailConfirmation();
    const { subscribe, isLoading: isSubscribing } = useSubscribeEmail();

    const priority = getEmailScreenPriority(user);

    if (priority !== 'problem') {
        return <Navigate to="/monitors" replace />;
    }

    if (!error) return null;

    const clickHandler = async() => {
        if (error === 'unsubscribed') {
            await subscribe(user?.email || '');
        } else {
            await confirmEmail();
        }
    };

    const emailBold = <span className={styles.bold}>{user?.email}</span>;
    const redText = (content: string) => {
        return (
            <span className={styles.red}>
                {content}
            </span>
        );
    };

    const profileLink = <Link to={AppUrls.SETTINGS} className={isTranslate() ? styles.enColor : undefined}>
        {t`профиле`}
    </Link>;
    const supportLink = (
        <a
            className={isTranslate() ? styles.enColor : undefined}
            href={AppUrls.SUPPORT_TICKET}
            target="_blank" rel="noopener noreferrer">
            {t`тех.поддержку`}
        </a>
    );

    const baseButton = {
        buttonText: t`Изменить E-mail`,
        onClick: () => setEmailModalOpen(true),
    };

    const AnswerList = ({ items }: { items: ReactNode[] }) => (
        <ol>
            {items.map((item, index) => (
                <li key={index}>{item}</li>
            ))}
        </ol>
    );

    const getEmailConfirmationErrorData  = (type: EmailErrorType): EmailErrorData => {
        const HeaderBase = ({ reason }: { reason: ReactNode }) => (
            <div>
                {t`Мы не смогли доставить вам письмо для подтверждения почты`}.
                <div>{t`Адрес`} {emailBold} {reason}.</div>
            </div>
        );
        switch (type) {
            case 'invalid':
                return {
                    gradient: styles.invalid,
                    ...baseButton,
                    textHeader: <HeaderBase reason={redText(t`недействителен или содержит ошибки`)} />,
                    answer: (
                        <AnswerList
                            items={[
                                <>
                                    {t`Проверьте правильность почтового адреса`} {emailBold} {t`в вашем`} {profileLink}.
                                </>,
                                <>
                                    {t`Если адрес неверный, измените его на корректный и подтвердите`}.
                                </>,
                            ]}
                        />
                    ),
                };
            case 'temporary_unavailable': {
                const button = {
                    buttonText: t`Отправить подтверждение`,
                    onClick: clickHandler,
                };

                return {
                    gradient: styles.temporaryunavailable,
                    ...button,
                    textHeader: <HeaderBase reason={redText(t`временно недоступен`)} />,
                    answer: (
                        <>
                            <AnswerList
                                items={[
                                    t`Попробуйте запросить подтверждение почты позже.`,
                                    <>
                                        {t`Если письмо подтверждения почты не приходит`}:
                                        <ul>
                                            <li>
                                                {/* eslint-disable-next-line max-len */}
                                                {t`Измените адрес`} {emailBold} {t`в вашем`} {profileLink} {t`и подтвердите его`};
                                            </li>
                                            <li>{t`Обратитесь в поддержку вашего почтового сервиса`}.</li>
                                        </ul>
                                    </>,
                                ]}
                            />
                            <Button
                                variant="gradient"
                                onClick={baseButton.onClick}
                                className={styles.button}
                            >
                                {baseButton.buttonText}
                            </Button>
                        </>
                    ),
                };
            }
            case 'permanent_unavailable':
                return {
                    gradient: styles.permanentunavailable,
                    ...baseButton,
                    textHeader: <HeaderBase reason={redText(t`недоступен`)} />,
                    answer: <span>
                        {t`Измените адрес`} {emailBold} {t`в вашем`} {profileLink} {t`и подтвердите его`}.
                    </span>,
                };
            case 'blocked':
                return {
                    gradient: styles.blocked,
                    ...baseButton,
                    textHeader: <div>{t`Адрес`} {emailBold} {redText(t`заблокирован нашей системой рассылки`)}.</div>,
                    answer: (
                        <AnswerList
                            items={[
                                <>
                                    {/* eslint-disable-next-line max-len */}
                                    {t`Если это произошло по ошибке, напишите нам в`} {supportLink} — {t`мы рассмотрим вашу ситуацию индивидуально`}.
                                </>,
                                <>{t`Измените адрес`} {emailBold} {t`в вашем`} {profileLink} {t`и подтвердите его`}.</>,
                            ]}
                        />
                    ),
                };
            case 'unsubscribed': {
                return {
                    gradient: styles.unsubscribed,
                    buttonText: isSubscribing ? t`Подписка...` : t`Подписаться`,
                    onClick: clickHandler,
                    textHeader: (
                        <div>
                            {t`Мы не смогли доставить вам письмо для подтверждения почты на адрес`} {emailBold}
                            <div>
                                {redText(t`Так как вы ранее отписались от наших рассылок`)}.
                            </div>
                        </div>),
                    answer: (
                        <AnswerList
                            items={[
                                <>{t`Подпишитесь на рассылку`}.</>,
                                <>{t`Подтвердить вашу почту`}.</>,
                            ]}
                        />
                    ),
                };
            }
            case 'complained':
                return {
                    gradient: styles.complained,
                    ...baseButton,
                    textHeader: (
                        <div>
                            {/* eslint-disable-next-line max-len */}
                            {t`Мы заметили, что вы отмечали наши письма как спам и жаловались на них. Пожалуйста, не делайте этого, такие действия ухудшают доставку наших писем другим пользователям`}.
                            <div
                                className={styles.red}>
                                {t`В связи с этим отправка писем на`} {emailBold} {t`заблокирована`}.
                            </div>
                        </div>
                    ),
                    answer: (
                        <AnswerList
                            items={[
                                <>
                                    {/* eslint-disable-next-line max-len */}
                                    {t`Если это произошло по ошибке, напишите нам в`} {supportLink} — {t`мы рассмотрим вашу ситуацию индивидуально`}.
                                </>,
                                <>{t`Измените адрес`} {emailBold} {t`в вашем`} {profileLink} {t`и подтвердите его`}.</>,
                            ]}
                        />
                    ),
                };
            default:
                throw new Error(` ${t`Тип ошибки не найден`}: ${type}`);
        }
    };

    const getEmailNotificationErrorData = (type: EmailErrorType): EmailErrorData => {
        switch (type) {
            case 'temporary_unavailable': {
                return {
                    gradient: styles.temporaryunavailableConfirmed,
                    textHeader: (
                        <div>
                            {t`Нам не удалось доставить вам письмо, так как адрес`} {emailBold}
                            <span className={styles.red}> {t`временно недоступен`}.</span>
                            <div>
                                {/* eslint-disable-next-line max-len */}
                                {t`Мы продолжаем попытки отправки, поскольку проблема с доступностью может решиться автоматически через некоторое время`}.
                            </div>
                        </div>
                    ),
                    answer: (
                        <>
                            {t`Если уведомления продолжают не приходить длительное время`}:
                            <ul>
                                <li>{t`Обратитесь в поддержку вашего почтового сервиса`};</li>
                                <li>
                                    {t`Измените адрес`} {emailBold} {t`в вашем`} {profileLink} {t`и подтвердите его`}.
                                </li>
                            </ul>
                            <Button
                                variant="gradient"
                                onClick={baseButton.onClick}
                                className={styles.button}
                            >
                                {baseButton.buttonText}
                            </Button>
                        </>
                    ),
                };
            }
            case 'permanent_unavailable':
                return {
                    gradient: styles.permanentunavailableConfirmed,
                    textHeader: (
                        <div>
                            {t`Нам не удалось доставить вам письмо, потому что адрес`}  {emailBold}
                            <span className={styles.red}> {t`недоступен`}.</span>
                            <div>{t`В связи с этим отправка писем на данный адрес прекращена`}.</div>
                        </div>),
                    answer: (<>
                        <AnswerList
                            items={[
                                // eslint-disable-next-line max-len
                                <>{t`Чтобы определить причину недоступности вашего почтового адреса, обратитесь в службу поддержки вашего почтового сервиса. После того как проблема будет устранена, сообщите нам в`} {supportLink}, {t`и мы вручную возобновим отправку писем`}.</>,
                                <>{t`Измените адрес`} {emailBold} {t`в вашем`} {profileLink} {t`и подтвердите его`}.</>,
                            ]}

                        />
                        <Button
                            variant="gradient"
                            onClick={baseButton.onClick}
                            className={styles.button}
                        >
                            {baseButton.buttonText}
                        </Button>
                    </>),
                };
            case 'blocked':
                return {
                    gradient: styles.blockedConfirmed,
                    textHeader: (
                        <div>
                            {t`Адрес`} {emailBold}
                            <span className={styles.red}> {t`заблокирован нашей системой рассылки`}</span>.
                            <div>{t`В связи с этим отправка писем на данный адрес прекращена`}.</div>
                        </div>
                    ),
                    answer: (<>
                        <AnswerList
                            items={[
                                // eslint-disable-next-line max-len
                                <>{t`Если это произошло по ошибке, напишите нам в`} {supportLink} — {t`мы рассмотрим вашу ситуацию индивидуально`}.</>,
                                <>{t`Измените адрес`} {emailBold} {t`в вашем`} {profileLink} {t`и подтвердите его`}.</>,
                            ]}

                        />
                        <Button
                            variant="gradient"
                            onClick={baseButton.onClick}
                            className={styles.button}
                        >
                            {baseButton.buttonText}
                        </Button>
                    </>),
                };
            case 'unsubscribed': {
                return {
                    gradient: styles.unsubscribedConfirmed,
                    textHeader: (
                        <div>
                            {t`Мы не смогли доставить вам письмо на адрес`} {emailBold}
                            <div>{redText(t`Так как вы ранее отписались от наших рассылок`)}.</div>
                        </div>
                    ),
                    answer: (
                        <>
                            <AnswerList items={[
                                <>{t`Если это произошло по ошибке подпишитесь на рассылку`}.</>,
                                // eslint-disable-next-line max-len
                                <>{t`Массовая отписка от рассылки может ухудшить доставку наших писем другим пользователям. Просьба не отказываться от рассылки таким способом. Если вы не хотите получать уведомления на почту, лучше всего деактивируйте адрес`} {emailBold} {t`в вашем`} {profileLink}.</>,
                            ]}/>
                            <Button
                                variant="gradient"
                                onClick={clickHandler}
                                className={styles.button}
                            >
                                {isSubscribing ? t`Подписка...` : t`Подписаться`}
                            </Button>
                        </>),
                };
            }
            case 'complained':
                return {
                    gradient: styles.complainedConfirmed,
                    textHeader: (
                        <div>
                            {/* eslint-disable-next-line max-len */}
                            {t`Мы заметили, что вы отмечали наши письма как спам и жаловались на них. Пожалуйста, не делайте этого, такие действия ухудшают доставку наших писем другим пользователям`}.
                            <div
                                className={styles.red}>
                                {t`В связи с этим отправка писем на`} {emailBold} {t`заблокирована`}.
                            </div>
                        </div>
                    ),
                    answer: (
                        <>
                            <AnswerList
                                items={[
                                    // eslint-disable-next-line max-len
                                    <>{t`Если это произошло по ошибке, напишите нам в`} {supportLink} — {t`мы рассмотрим вашу ситуацию индивидуально`}.</>,
                                    // eslint-disable-next-line max-len
                                    <>{t`Просьба не отказываться от рассылки таким способом. Если вы не хотите получать уведомления на почту, лучше всего деактивируйте адрес`} {emailBold} {t`в вашем`} {profileLink}.</>,
                                    // eslint-disable-next-line max-len
                                    <>{t`Измените адрес`} {emailBold} {t`в вашем`} {profileLink} {t`и подтвердите его`}.</>,
                                ]}
                            />
                            <Button
                                variant="gradient"
                                onClick={baseButton.onClick}
                                className={styles.button}
                            >
                                {baseButton.buttonText}
                            </Button>
                        </>
                    ),
                };
            default:
                throw new Error(`${t`Тип ошибки не найден`}: ${type}`);
        }
    };

    const getErrorData = emailConfirmed ? getEmailNotificationErrorData : getEmailConfirmationErrorData;
    const { buttonText, onClick, textHeader, answer, gradient } = getErrorData(error);

    return (
        <section className={cn(styles.section, gradient)}>
            <SummaryHeader />
            <HeaderEmail
                title={
                    emailConfirmed
                        ? t`Проблема с почтой блокирует отправку уведомлений!`
                        : t`Проблема с почтой блокирует завершение регистрации!`
                }
                onClick={onClick}
                buttonText={buttonText}
                icon={Email}
                button={!emailConfirmed}
            >
                {textHeader}
            </HeaderEmail>
            <AskEmail>{answer}</AskEmail>
            {
                emailConfirmed ? <></> : <AnswerEmail/>
            }
            <ChangeEmailModal open={emailModalOpen} handleClose={() => setEmailModalOpen(false)} />
        </section>
    );
};

export default EmailProblem;